import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import {
  getNoticeConfig,
  saveNoticeConfig,
  type NoticeConfigValues,
  type ResolvedNoticeConfig,
} from '@/api/notices';
import type { TeamPanelHandle } from '@/components/teamPanelHandle';
import { NoticeConfigForm } from '@/components/notices/NoticeConfigForm';

// TeamNoticesPanel is the "Aviseringar" tab inside TeamSheet: the per-team
// OVERRIDE layer. Each field left on "inherit" falls through to the team's
// category (then the flow default); the resolved config drives the inherited
// hints. Exposes the TeamPanelHandle commit() so the sheet's shared Save
// persists it alongside the other tabs.

interface Props {
  slug: string;
  teamID: string;
}

export const TeamNoticesPanel = forwardRef<TeamPanelHandle, Props>(function TeamNoticesPanel(
  { slug, teamID },
  ref,
) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [values, setValues] = useState<NoticeConfigValues>({});
  const [resolved, setResolved] = useState<ResolvedNoticeConfig | undefined>(undefined);
  const [inherited, setInherited] = useState<ResolvedNoticeConfig | undefined>(undefined);

  const configQuery = useQuery({
    queryKey: ['team-notice-config', slug, teamID],
    queryFn: () => getNoticeConfig(token, slug, teamID),
    enabled: !!token && !!slug && !!teamID,
  });

  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !configQuery.data) return;
    setValues(configQuery.data.values);
    setResolved(configQuery.data.resolved);
    setInherited(configQuery.data.inherited);
    seededRef.current = true;
  }, [configQuery.data]);

  useImperativeHandle(
    ref,
    () => ({
      async commit() {
        await saveNoticeConfig(token, slug, teamID, values);
        void queryClient.invalidateQueries({ queryKey: ['team-notice-config', slug, teamID] });
        // Preview/collection on the Notices page read the resolved config.
        void queryClient.invalidateQueries({ queryKey: ['notices-config', slug, teamID] });
        return true;
      },
    }),
    [token, slug, teamID, values, queryClient],
  );

  if (configQuery.isPending) {
    return <p className="text-sm text-muted-foreground">{t('notices.config.loading')}</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t('notices.config.teamIntro')}</p>
      <NoticeConfigForm
        values={values}
        onChange={setValues}
        resolved={resolved}
        inherited={inherited}
      />
    </div>
  );
});
